import { useState } from "react";
import {
  Table,
  ScrollArea,
  Text,
  Group,
  Button,
  Paper,
  Stack,
  Alert,
  Divider,
  Box,
} from "@mantine/core";
import { Download, AlertTriangle } from "lucide-react";
import { EmailLog, downloadFile } from "../../services/logService.js";

interface CsvPreviewProps {
  log: EmailLog;
}

/**
 * オブジェクトからパス指定で値を取得するヘルパー
 */
function getCellValue(obj: Record<string, unknown> | undefined | null, path: string, detailItem?: Record<string, unknown>): string {
  if (!obj) return "";

  const pathStr = String(path);
  if (pathStr.includes("[]")) {
    if (!detailItem) return "";
    const fieldPath = pathStr.split("[].")[1];
    if (!fieldPath) return String(detailItem || "");
    return String(detailItem[fieldPath] ?? "");
  }

  const parts = pathStr.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return "";
    current = (current as Record<string, unknown>)[part];
  }
  return current != null ? String(current) : "";
}

export function CsvPreview({ log }: CsvPreviewProps) {
  const [downloading, setDownloading] = useState(false);

  if (!log.csvOutput) return null;

  const handleDownload = async () => {
    if (!log.csvOutput) return;
    try {
      setDownloading(true);
      await downloadFile(log.csvOutput.storagePath, log.csvOutput.filename);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const columns = log.csvOutput.columns || [];
  // 配列を含むパス（[] を含むもの）を探し、そのベースパスを取得
  const detailArrayPath = columns.find(col => String(col.jsonPath).includes("[]"))?.jsonPath as string | undefined;
  const basePath = detailArrayPath?.split("[]")[0];

  // OCR結果から行データを抽出
  const data = log.ocrResult?.structuredData;
  const detailRows = (basePath && data && Array.isArray((data as Record<string, unknown>)[basePath]))
    ? (data as Record<string, unknown>)[basePath] as Record<string, unknown>[]
    : [];

  return (
    <Paper withBorder p="xs" mt="xs" bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))">
      <Stack gap="xs">
        <Group justify="space-between">
          <Stack gap="2px">
            <Text size="xs" fw={700}>{log.csvOutput.filename}</Text>
            <Text size="10px" c="dimmed">{log.csvOutput.rowCount}件のデータ • {log.csvOutput.encoding}</Text>
          </Stack>
          <Button
            size="compact-xs"
            variant="light"
            leftSection={<Download size={14} />}
            onClick={handleDownload}
            loading={downloading}
          >
            CSVをダウンロード
          </Button>
        </Group>

        {log.csvOutput.validationErrors && log.csvOutput.validationErrors.length > 0 && (
          <Alert color="orange" py="5px" styles={{ message: { fontSize: '10px' } }} icon={<AlertTriangle size={14} />}>
            <Text size="10px" fw={600}>バリデーション警告:</Text>
            <Stack gap={0}>
              {log.csvOutput.validationErrors.map((err, i) => (
                <Text key={i} size="10px">• {err}</Text>
              ))}
            </Stack>
          </Alert>
        )}

        {data && columns.length > 0 ? (
          <Box mt="xs">
            <Divider label="データプレビュー (実際のCSV構成)" labelPosition="center" mb="sm" />

            <ScrollArea>
              <Table variant="simple" withTableBorder withColumnBorders verticalSpacing="4px" horizontalSpacing="xs" style={{ fontSize: '10px', minWidth: '800px' }}>
                <Table.Thead bg="light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-7))">
                  <Table.Tr>
                    {columns.sort((a, b) => ((a.order || 0) as number) - ((b.order || 0) as number)).map((col, j) => (
                      <Table.Th key={j}>{String(col.headerName || "")}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {detailRows.length > 0 ? (
                    detailRows.map((detailItem, rowIdx) => (
                      <Table.Tr key={rowIdx}>
                        {columns.map((col, k) => (
                          <Table.Td key={k}>
                            {getCellValue(data as Record<string, unknown>, String(col.jsonPath), detailItem)}
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))
                  ) : (
                    <Table.Tr>
                      {columns.map((col, m) => (
                        <Table.Td key={m}>
                          {getCellValue(data as Record<string, unknown>, String(col.jsonPath))}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            <Text size="10px" c="dimmed" mt="xs" ta="right">
              ※このプレビューは取引先ごとの出力設定に基づいて生成されています
            </Text>
          </Box>
        ) : (
          <Text size="xs" c="dimmed" ta="center" py="xl">表示できるデータがありません</Text>
        )}
      </Stack>
    </Paper>
  );
}
